/**
 * SASADM-0153 — shared brand-colour control: a swatch that opens the native
 * colour picker, a two-way monospace hex text field, and a copy button.
 *
 * Accepts `#rgb` / `#rrggbb` (with or without `#`, any case) and always hands
 * `onChange` a lowercase `#rrggbb`. Invalid text shows an inline error and is
 * never passed to `onChange`. Blank is valid only when the field mounted with
 * no value (the key stays unset); clearing a set colour is invalid.
 *
 * SASADM-0154 — optional **Auto** mode for derived theme values: with `auto`
 * and no value the field shows an "Auto" chip and the server's `resolvedValue`
 * (swatch + placeholder); with a value it offers "Reset to auto" (`onReset`),
 * and blank is always valid (clearing it resets too).
 */
import { Box, Button, Chip, Stack, TextField } from '@mui/material';
import { type ChangeEvent, useEffect, useEffectEvent, useState } from 'react';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { useCopy } from '@/hooks/useCopy';

const HEX_REGEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Normalises a hex colour to lowercase `#rrggbb`, or `null` when it is not one. */
function normalizeHex(input: string): string | null {
  const trimmed = input.trim();
  if (!HEX_REGEX.test(trimmed)) return null;
  const digits = trimmed.replace(/^#/, '');
  const six =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits;
  return `#${six.toLowerCase()}`;
}

export interface HexColorFieldProps {
  label: string;
  value: string | null | undefined;
  onChange: (hex: string) => void;
  /** Reports the field's validity so the parent can block Save; reports valid on unmount. */
  onValidityChange?: (valid: boolean) => void;
  /** Auto mode: an unset value means "derived" and shows `resolvedValue`. */
  auto?: boolean;
  /** The server-resolved colour an Auto field displays while unset. */
  resolvedValue?: string | undefined;
  /** "Reset to auto" — the parent clears the value (null / absent). */
  onReset?: () => void;
  disabled?: boolean;
}

export function HexColorField({
  label,
  value,
  onChange,
  onValidityChange,
  auto = false,
  resolvedValue,
  onReset,
  disabled = false,
}: HexColorFieldProps) {
  const { t } = useCopy();
  const [text, setText] = useState(value ?? '');
  const [prevValue, setPrevValue] = useState(value);
  // Blank is only a valid state for a field that had no value to begin with
  // (or, in Auto mode, always — blank means "derived").
  const [startedUnset] = useState(() => !value);
  const blankAllowed = startedUnset || auto;

  // Follow an external value change (a pick, a reloaded brand) unless the text
  // already denotes it — so typing `#0f6` doesn't get rewritten mid-entry.
  if (value !== prevValue) {
    setPrevValue(value);
    if (normalizeHex(text) !== (value || null)) setText(value ?? '');
  }

  const hex = normalizeHex(text);
  const blank = text.trim() === '';
  const valid = hex !== null || (blank && blankAllowed);
  const isAuto = auto && !value && blank;
  const autoHex = isAuto && resolvedValue ? normalizeHex(resolvedValue) : null;
  const swatchHex = hex ?? autoHex;

  const reportValidity = useEffectEvent((v: boolean) => onValidityChange?.(v));
  useEffect(() => {
    reportValidity(valid);
    // An unmounted field's draft is discarded, so it no longer blocks Save.
    return () => reportValidity(true);
  }, [valid]);

  function handleTextChange(next: string) {
    setText(next);
    const normalized = normalizeHex(next);
    if (normalized !== null) {
      if (normalized !== value) onChange(normalized);
    } else if (next.trim() === '' && auto && value) {
      onReset?.();
    } else if (next.trim() === '' && startedUnset && value) {
      onChange('');
    }
  }

  function handlePick(picked: string) {
    const normalized = normalizeHex(picked);
    if (normalized === null) return;
    setText(normalized);
    if (normalized !== value) onChange(normalized);
  }

  const notSet = t('common.hexColor.notSet', 'Not set');

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Box sx={{ position: 'relative', width: 40, height: 40, flexShrink: 0, mt: 0.5 }}>
        <Box
          role="img"
          aria-label={swatchHex ?? notSet}
          title={swatchHex ?? notSet}
          sx={(theme) => ({
            width: '100%',
            height: '100%',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            // Neutral stripes for "not set" — never black.
            backgroundImage: `repeating-linear-gradient(45deg, ${theme.palette.action.selected} 0 4px, ${theme.palette.action.hover} 4px 8px)`,
          })}
          style={swatchHex ? { backgroundColor: swatchHex, backgroundImage: 'none' } : undefined}
        />
        <Box
          component="input"
          type="color"
          aria-label={t('common.hexColor.picker', '{{label}} colour picker', { label })}
          disabled={disabled}
          value={swatchHex ?? '#ffffff'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handlePick(e.target.value)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            p: 0,
            border: 0,
            opacity: 0,
            cursor: disabled ? 'default' : 'pointer',
          }}
        />
      </Box>
      <TextField
        fullWidth
        label={label}
        value={text}
        disabled={disabled}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={() => {
          if (hex) setText(hex);
        }}
        error={!valid}
        helperText={valid ? '' : t('common.hexColor.invalid', 'Enter a hex colour like #0f6e56')}
        placeholder={autoHex ?? (startedUnset ? notSet : undefined)}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { spellCheck: false, style: { fontFamily: 'monospace' } },
        }}
      />
      <Box sx={{ mt: 1 }}>
        <CopyToClipboardButton
          value={hex ?? ''}
          ariaLabel={t('common.hexColor.copy', 'Copy {{label}} hex', { label })}
          disabled={!hex}
        />
      </Box>
      {auto ? (
        <Box sx={{ mt: 1, flexShrink: 0 }}>
          {isAuto ? (
            <Chip size="small" label={t('common.hexColor.auto', 'Auto')} />
          ) : (
            <Button
              size="small"
              onClick={() => {
                setText('');
                onReset?.();
              }}
              disabled={disabled}
              aria-label={t('common.hexColor.resetLabel', 'Reset {{label}} to auto', { label })}
            >
              {t('common.hexColor.reset', 'Reset to auto')}
            </Button>
          )}
        </Box>
      ) : null}
    </Stack>
  );
}
